// Must be nested under <GatedCommunityOverlay>: when the community is also
// inaccessible this defers to the outer wrapper to avoid both overlays rendering
// their gate cards (the inner one would show up blurred behind the outer).
import type { FC, ReactNode } from 'react';

import type { GateScope } from '~/constants/community';
import { useNectarImpression } from '~/hooks/useNectarImpression';
import { CommunitySendableType } from '~/interfaces/community/community';
import { NectarPageBlock } from '~/utils/nectarPageBlock';

import { useCommunityAccess } from '../_hooks/useCommunityAccess';
import { useCommunityMembership } from '../_hooks/useCommunityMembership';
import { type SpaceAccessTarget, useSpaceAccess } from '../_hooks/useSpaceAccess';
import { GatedFeedOverlay } from './GatedFeedOverlay';

interface GatedSpaceOverlayProps {
  space: SpaceAccessTarget | null;
  scope: GateScope;
  children: ReactNode;
}

export const GatedSpaceOverlay: FC<GatedSpaceOverlayProps> = ({ space, scope, children }) => {
  const { canAccessSpace, isSpaceClosed, isSpaceConditionLocked } = useSpaceAccess();
  const { isCommunityAccessible } = useCommunityAccess();
  const { isMember } = useCommunityMembership();

  // Gate only when the community is open (otherwise the outer overlay wins),
  // the space is gated, and the viewer doesn't have access.
  const isSpaceGated = !canAccessSpace(space);
  const showFeed = !isMember || !isCommunityAccessible || !isSpaceGated;
  // The owner's message replaces the upgrade card for a member who doesn't meet the channel's condition, and a closed
  // channel has nothing to sell.
  const showsUpgradeCard = !showFeed && !(space && (isSpaceConditionLocked(space) || isSpaceClosed(space)));

  useNectarImpression(
    {
      pageBlock: NectarPageBlock.CommunityFeedUpgradeBanner,
      sendableId: space?.id ?? '',
      sendableType: CommunitySendableType.CommunitySpace,
    },
    {
      fireWhenInViewport: false,
      enabled: showsUpgradeCard,
    },
  );

  if (showFeed) return children;

  return (
    <GatedFeedOverlay scope={scope} space={space}>
      {children}
    </GatedFeedOverlay>
  );
};
